import React, {Component} from 'react'
import {createTypes} from 'reduxsauce'
import propTypes from 'prop-types'
import {isNil} from 'lodash'
import iconNames from '../../theme/icons'
import theme from 'theme/index'
import themeVars from 'theme/themeVars'
import {createSizes} from 'theme/sizes'
import {getIconUrl} from 'helpers/cdn'
import {getSizeProp} from 'helpers/component'
import {getImageDimensions} from 'helpers/ui'

const iconStyles = createTypes(`
  PLAIN
  SQUARE
  CIRCLE
  SQUARE_INVERSE
  CIRCLE_INVERSE
  ORIGINAL
`)

const iconTheme = {
  backgroundColor: theme.icon.backgroundColor,
  foregroundColor: theme.icon.foregroundColor,
  internalRatio: theme.icon.internalRatio,
  borderRadius: theme.icon.borderRadius,
  color: theme.icon.color,
  disabledColor: theme.icon.disabledColor,
  size: theme.icon.size,
  margin: theme.icon.margin,
}

const iconSizes = createSizes(8, 12, 20, 20, 38, 68)

/**
 * Example usage <Icon name="pin_filled" size={44}/>
 **/
class Icon extends Component {
  constructor(props) {
    super(props)
    this.state = this.getInitialState()
    this._isMounted = false
  }

  getInitialState = () => ({
    naturalWidth: null,
    naturalHeight: null,
    naturalSizeLoaded: false,
  })

  componentDidMount() {
    this.updateImageSize()
    this._isMounted = true
  }

  componentDidUpdate(prevProps) {
    if (prevProps.name !== this.props.name) {
      this.updateImageSize()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  updateImageSize = () => {
    getImageDimensions(this.getSource(), dimensions => {
      if (this._isMounted) {
        this.setState({
          naturalWidth: dimensions.width,
          naturalHeight: dimensions.height,
          naturalSizeLoaded: true,
        })
      }
    })
  }

  getSource = () => {
    return getIconUrl(this.props.name)
  }

  getColour = () => {
    const {color, disabled, lightColor, primary} = this.props

    if (!isNil(color)) return color
    if (disabled) return iconTheme.disabledColor
    if (lightColor) return themeVars.palette.lightIconColor
    if (primary) return themeVars.palette.primary1Color

    return iconTheme.color
  }

  getSize = () => {
    const requestedSize = getSizeProp(this.props)
    switch (typeof requestedSize) {
      case 'string':
        return iconSizes[requestedSize]
      case 'number':
        return requestedSize
      default:
        return iconSizes.DEFAULT
    }
  }

  getStyles = () => {
    const style = this.props.style || {}
    const iconStyle = this.props.iconStyle || {}
    const styleType = this.props.type || iconStyles.PLAIN
    const backgroundColor = this.props.backgroundColor || iconTheme.backgroundColor || 'transparent'
    const foregroundColor = this.props.backgroundColor || iconTheme.foregroundColor || 'white'
    const iconSrc = this.getSource()
    const margin = iconTheme.margin || 4
    const opacity = this.props.opacity || 1
    const color = this.getColour()
    const size = this.getSize()

    const borderWidth = this.props.borderWidth
    let borderRadius = this.props.borderRadius || iconTheme.borderRadius || '4%'
    const borderColor = this.props.borderColor
    const borderStyle = this.props.borderStyle || 'solid'

    const calcBorderWidth = (borderWidth) => {
      if (typeof borderWidth === 'undefined') {
        return Math.max(size * 0.02, 1)
      }
      return borderWidth
    }

    const calcSize = (size) => ({
      minWidth: size,
      minHeight: size,
      width: size,
      height: size,
    })

    let container = {
      boxSizing: 'border-box',
      flexBasis: 'auto',
      minWidth: size,
      minHeight: size,
      width: size,
      height: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor,
      cursor: (typeof this.props.onClick === 'function') ? 'pointer' : null,
      transitionDuration: '200ms',
      margin,
      opacity,
      ...style,
    }

    let icon = {
      flexBasis: 'auto',
      WebkitMask: 'url(' + iconSrc + ') 50% 50%/100% no-repeat',
      mask: 'url(' + iconSrc + ') 50% 50%/100% no-repeat',
      transitionDuration: '200ms',
      backgroundColor: color,
      ...iconStyle,
    }

    const iconSizeRatio = iconTheme.internalRatio || 0.66
    switch (styleType) {
      case iconStyles.PLAIN:
        icon = Object.assign(icon, calcSize(size))
        break

      case iconStyles.SQUARE:
        container = Object.assign(container, {
          borderWidth: calcBorderWidth(borderWidth),
          borderRadius,
          borderColor: color,
          borderStyle,
        })
        icon = Object.assign(icon, calcSize(parseInt(size * iconSizeRatio)))
        break

      case iconStyles.CIRCLE:
        borderRadius = '50%'
        container = Object.assign(container, {
          borderWidth: calcBorderWidth(borderWidth),
          borderRadius,
          borderColor: color,
          borderStyle,
        })
        icon = Object.assign(icon, calcSize(parseInt(size * iconSizeRatio)))
        break

      case iconStyles.SQUARE_INVERSE:
        container = Object.assign(container, {
          backgroundColor: color,
          borderRadius: borderRadius,
          borderColor: borderColor,
          borderWidth: borderColor ? calcBorderWidth(borderWidth) : null,
          borderStyle: borderColor ? borderStyle : null,
        })
        icon = Object.assign(icon, {
          backgroundColor: foregroundColor,
        }, calcSize(parseInt(size * iconSizeRatio)))
        break

      case iconStyles.CIRCLE_INVERSE:
        borderRadius = '50%'
        container = Object.assign(container, {
          backgroundColor: color,
          borderRadius: borderRadius,
          borderColor: borderColor,
          borderWidth: borderColor ? calcBorderWidth(borderWidth) : null,
          borderStyle: borderColor ? borderStyle : null,
        })
        icon = Object.assign(icon, {
          backgroundColor: foregroundColor,
        }, calcSize(parseInt(size * iconSizeRatio)))
        break

      case iconStyles.ORIGINAL:
        icon = {
          flexBasis: 'auto',
          background: 'url(' + iconSrc + ') 50% 50%/100% no-repeat',
        }
        icon = Object.assign(icon, calcSize(size))
        break
    }

    return Object.assign({}, {container, icon}, style)
  }

  getImageDimensions = () => {
    const {naturalWidth, naturalHeight, naturalSizeLoaded} = this.state
    const size = this.getSize()

    // Fit the image within a square specified by size
    return naturalSizeLoaded && naturalWidth > naturalHeight
      ? {width: size}
      : {height: size}
  }

  render() {
    const {name, onMouseOver, onMouseOut, noFill, disabled, className, onClick, type} = this.props

    const styles = this.getStyles()
    const isOriginal = type === iconStyles.ORIGINAL

    return noFill
      ? (
        <img
          alt={name}
          className={className}
          src={this.getSource()}
          {...this.getImageDimensions()}
        />
      )
      : (
        <div
          className={`icon icon-${name}${isOriginal ? ' icon-original' : ''} ${className}`}
          style={styles.container}
          onClick={disabled ? null : onClick}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          <div style={styles.icon} className={`icon-icon`}/>
        </div>
      )
  }
}

Icon.propTypes = {
  alt: propTypes.string,
  name: propTypes.string,
  noFill: propTypes.bool,
  color: propTypes.string,
  type: propTypes.string,
  backgroundColor: propTypes.string,
  size: propTypes.oneOfType([propTypes.string, propTypes.number]),
  borderWidth: propTypes.number,
  borderRadius: propTypes.number,
  borderColor: propTypes.string,
  borderStyle: propTypes.string,
  disabled: propTypes.bool,
  lightColor: propTypes.bool, // shortcut for using muiTheme.palette.lightIconColor
  primary: propTypes.bool,    // shortcut for using muiTheme.palette.primary1Color
  style: propTypes.object,
  iconStyle: propTypes.object,
  opacity: propTypes.number,
  className: propTypes.string,
  onClick: propTypes.func,
  onMouseOver: propTypes.func,
  onMouseOut: propTypes.func,
}

Icon.defaultProps = {
  alt: '',
  name: 'question',
  onClick: null,
  noFill: false,
  disabled: false,
}

/**
 * Example usage <CircleIcon name="pin_filled" />
 * <CircleIcon name="pin_filled" inverse />
 **/
const CircleIcon = ({inverse, ...props}) => {
  const type = (inverse === true) ? iconStyles.CIRCLE_INVERSE : iconStyles.CIRCLE
  return <Icon {...props} type={type}/>
}
CircleIcon.propTypes = {
  inverse: propTypes.bool,
}

const SquareIcon = ({inverse, ...props}) => {
  const type = (inverse === true) ? iconStyles.SQUARE_INVERSE : iconStyles.SQUARE
  return <Icon {...props} type={type}/>
}
SquareIcon.propTypes = {
  inverse: propTypes.bool,
}

export default Icon

export {
  iconStyles,
  CircleIcon,
  SquareIcon,
  iconNames,
}
